import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FilePlus2, ChevronRight, ClipboardList } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MaturityBadge } from "@/components/AgenticVisuals";

export default function CandidatePortal() {
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["my-assessments"],
    queryFn: async () => (await api.get("/assessments")).data,
    refetchInterval: (query) => (query.state.data?.some((a) => a.status === "processing") ? 4000 : false),
  });

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Mis evaluaciones</h1>
          <p className="text-slate-400 mt-1.5">Tu historial de evaluaciones de madurez agentic.</p>
        </div>
        <Link to="/app/assessment/new">
          <Button data-testid="new-assessment-btn" className="gap-2 bg-primary text-slate-950 hover:bg-cyan-400">
            <FilePlus2 size={18} /> Nueva evaluación
          </Button>
        </Link>
      </div>

      {isLoading ? (
        <div className="text-slate-500">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-800 bg-slate-900/40 p-12 text-center">
          <ClipboardList className="mx-auto text-slate-600" size={40} strokeWidth={1.5} />
          <h3 className="mt-4 font-heading text-xl font-medium">Aún no tienes evaluaciones</h3>
          <p className="text-slate-400 mt-1.5 max-w-md mx-auto">Completa el cuestionario para descubrir tu Agentic Productivity Index.</p>
          <Link to="/app/assessment/new">
            <Button data-testid="empty-new-btn" className="mt-6 gap-2 bg-primary text-slate-950 hover:bg-cyan-400">
              <FilePlus2 size={18} /> Empezar
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((a, i) => (
            <motion.div
              key={a.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <Link
                to={`/app/assessment/${a.id}`}
                data-testid={`assessment-card-${a.id}`}
                className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-900 p-5 hover:bg-slate-800/60 transition-colors duration-200"
              >
                {(() => {
                  const isDraft = a.status === "draft";
                  const isError = a.status === "error";
                  const label = isDraft ? "Borrador" : isError ? "Error al procesar" : a.status === "processing" ? "Procesando…" : "En revisión";
                  const tone = isDraft ? "amber" : isError ? "red" : a.status === "processing" ? "cyan" : "emerald";
                  const iconClass = tone === "emerald" ? "bg-emerald-500/10 text-emerald-400" : tone === "cyan" ? "bg-cyan-500/10 text-cyan-400" : tone === "red" ? "bg-red-500/10 text-red-400" : "bg-amber-500/10 text-amber-400";
                  const pillClass = tone === "emerald" ? "border-emerald-500/40 text-emerald-400" : tone === "cyan" ? "border-cyan-500/40 text-cyan-400" : tone === "red" ? "border-red-500/40 text-red-400" : "border-amber-500/40 text-amber-400";
                  return (
                    <>
                      <div className="flex items-center gap-5">
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center ${iconClass}`}>
                          <ClipboardList size={18} />
                        </div>
                        <div>
                          <div className="font-medium text-slate-100">{a.target_role}</div>
                          <div className="text-xs text-slate-500 font-mono">{new Date(a.created_at).toLocaleDateString("es-ES")}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span data-testid="candidate-status" className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${pillClass}`}>
                          {label}
                        </span>
                        <ChevronRight className="text-slate-600" size={18} />
                      </div>
                    </>
                  );
                })()}
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
